import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button, Form} from "react-bootstrap";
import {useAlert} from "react-alert";
import QuestionField from "./QuestionField";
import {QUESTIONS_BY_TYPE} from "./questionnaireDefinitions";
import {
  cleanAnswers,
  isCheckTextFilled,
  isVisible,
  validateAnswers,
  visibleQuestions,
} from "./questionnaireEngine";
import {getTermoConsentimento} from "../../../utils/CompanyOriginUtils";
import "./QuestionnaireForm.css";

const TERM_ERROR = "É necessário concordar com os termos para prosseguir.";

/**
 * Formulário dinâmico de um questionário (aposentadoria ou pensão).
 *
 * Mantém as respostas num objeto plano; perguntas condicionais aparecem/somem conforme
 * `showIf`. No envio, valida só o que está visível e entrega o JSON limpo (sem as respostas
 * escondidas) para `onSubmit(answers)`.
 *
 * Props:
 *   type            "APOSENTADORIA" | "PENSAO"
 *   initialAnswers  respostas já salvas (edição)
 *   disabled        somente leitura
 *   requireTerm     exibe o Termo de Compromisso (cadastro de cliente)
 *   submitLabel     texto do botão
 *   onSubmit        (answers) => void | Promise
 *   onCancel        opcional — exibe botão "Cancelar"
 */
function QuestionnaireForm({type, initialAnswers, disabled, requireTerm, submitLabel, onSubmit, onCancel}) {
  const alert = useAlert();
  const questions = QUESTIONS_BY_TYPE[type];
  const [answers, setAnswers] = useState(() => ({...(initialAnswers || {})}));
  const [errors, setErrors] = useState({});
  const [termAccepted, setTermAccepted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // o pai costuma fechar o formulário ao salvar — não mexer no estado depois de desmontado
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);
  // checkTexts marcados (índice da pergunta -> boolean); começam marcados se já tiverem valor
  const [openChecks, setOpenChecks] = useState(() =>
    Object.fromEntries(
      questions
        .map((q, i) => [i, q.type === "checkText" && isCheckTextFilled(q, initialAnswers || {})])
        .filter(([, open]) => open)
    )
  );

  const onChange = useCallback((name, value) => {
    setAnswers((prev) => ({...prev, [name]: value}));
    setErrors((prev) => {
      if (!Object.keys(prev).some((key) => key === name || key.startsWith(`${name}.`))) return prev;
      const next = {...prev};
      Object.keys(next).forEach((key) => {
        if (key === name || key.startsWith(`${name}.`)) delete next[key];
      });
      return next;
    });
  }, []);

  const onToggleCheck = useCallback((index, checked) => {
    setOpenChecks((prev) => ({...prev, [index]: checked}));
    if (!checked) {
      // desmarcar limpa os campos — no legado, campo vazio = "Não"
      questions[index].fields.forEach((f) => onChange(f.name, ""));
    }
  }, [questions, onChange]);

  const shown = useMemo(() => visibleQuestions(questions, answers), [questions, answers]);

  async function handleSubmit(event) {
    event.preventDefault();
    const found = validateAnswers(questions, answers, openChecks);
    // checkText escondido/desmarcado não deve deixar resposta pendurada
    const toSend = {...answers};
    questions.forEach((q, i) => {
      if (q.type === "checkText" && (!openChecks[i] || !isVisible(q, answers))) {
        q.fields.forEach((f) => { toSend[f.name] = ""; });
      }
    });
    const termError = requireTerm && termAccepted !== true;
    setErrors(found);
    if (Object.keys(found).length || termError) {
      alert.show("Verifique os campos destacados no questionário.", {type: "error"});
      const first = Object.keys(found)[0];
      const element = first && document.getElementById(`q-${first}`);
      if (element) element.scrollIntoView({behavior: "smooth", block: "center"});
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(cleanAnswers(questions, toSend));
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  }

  return (
    <Form noValidate onSubmit={handleSubmit} className="questionnaire-form">
      {shown.map((question) => {
        const index = questions.indexOf(question);
        return (
          <div key={question.name || `${question.type}-${index}`}
               className={question.half ? "questionnaire-half" : undefined}>
            <QuestionField
              question={question}
              index={index}
              answers={answers}
              errors={errors}
              checkOpen={Boolean(openChecks[index])}
              disabled={disabled}
              onChange={onChange}
              onToggleCheck={onToggleCheck}
            />
          </div>
        );
      })}

      {requireTerm && (
        <Form.Group className="mb-3">
          <h6 className="questionnaire-section">Termo de Compromisso</h6>
          <a href={getTermoConsentimento()} target="_blank" rel="noopener noreferrer">
            Visualizar Termo de Compromisso
          </a>
          <p className="mb-1 mt-2">
            Declaro que as informações acima prestadas são verdadeiras, e assumo a inteira responsabilidade
            pelas mesmas. <span className="is-required">*</span>
          </p>
          {["Concordo", "Não concordo"].map((option) => (
            <Form.Check key={option} inline type="radio" id={`q-term-${option}`} name="q-term" label={option}
                        checked={termAccepted === (option === "Concordo")} disabled={disabled}
                        onChange={() => setTermAccepted(option === "Concordo")}/>
          ))}
          {termAccepted === false && <div className="questionnaire-error">{TERM_ERROR}</div>}
        </Form.Group>
      )}

      {!disabled && (
        <div className="questionnaire-actions">
          {onCancel && (
            <Button variant="outline-secondary" onClick={onCancel} disabled={submitting}>Cancelar</Button>
          )}
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? "Salvando..." : submitLabel}
          </Button>
        </div>
      )}
    </Form>
  );
}

export default QuestionnaireForm;
